import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { KeyRound, Laptop, Loader2, ShieldCheck, ShieldOff } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  TextField,
} from "@fleet/ui";
import { api, createApiQuery, fetchApi } from "@/lib/api";
import { QueryErrorState } from "@/components/ui/query-error-state";
import type { components } from "@bodega/shared";
import { generateId } from "@fleet/utils";
import { useI18n } from "@/providers/i18n-provider";

type DeviceView = components["schemas"]["DeviceView"];
type PairingView = components["schemas"]["PairingView"];

/**
 * Control plane of the PRIVATE multi-device cloud (P2P): issue a one-shot
 * pairing code for a new device, join with a code, review the trusted device
 * set and revoke any device. Trust changes are enforced at the datapath by the
 * sync engine (a revoked device stops being served immediately); this page
 * only manages the trust/topology stored server-side — file content never
 * touches the server.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function PrivateCloudDevices() {
  const { t } = useI18n();

  const devices = createApiQuery<DeviceView[] | null, components["schemas"]["ErrorModel"]>(
    () => ["p2p-cloud", "devices"],
    async () => api.GET("/api/p2p-cloud/devices", {}),
  );

  // ── Issue a pairing code (this device invites a new one) ──────────────────
  const [pairing, setPairing] = createSignal<PairingView | null>(null);
  const [issuing, setIssuing] = createSignal(false);
  const issuePairing = async () => {
    setIssuing(true);
    try {
      const view = await fetchApi(api.POST("/api/p2p-cloud/pairing", { body: {} }));
      setPairing(view);
    } finally {
      setIssuing(false);
    }
  };

  // ── Join with a code (this device becomes trusted) ─────────────────────────
  const [joinToken, setJoinToken] = createSignal("");
  const [joinName, setJoinName] = createSignal("");
  const [joining, setJoining] = createSignal(false);
  const [joined, setJoined] = createSignal<DeviceView | null>(null);
  const [joinError, setJoinError] = createSignal<string | null>(null);
  const join = async () => {
    if (!joinToken().trim() || !joinName().trim() || joining()) {
      return;
    }
    setJoining(true);
    setJoinError(null);
    try {
      const view = await fetchApi(
        api.POST("/api/p2p-cloud/pair", {
          body: {
            token: joinToken().trim(),
            device_id: generateId(),
            name: joinName().trim(),
          },
        }),
      );
      setJoined(view);
      setJoinToken("");
      void devices.refetch();
    } catch {
      setJoinError(t(msg`Invalid or expired pairing code.`));
    } finally {
      setJoining(false);
    }
  };

  // Revocation keys on the device's STABLE id (`device_id`), the same identity
  // the datapath trust check uses — not the DB row id.
  const revoke = async (deviceId: string) => {
    await fetchApi(
      api.POST("/api/p2p-cloud/devices/{device_id}/revoke", {
        params: { path: { device_id: deviceId } },
      }),
    );
    void devices.refetch();
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-bold tracking-tight">{t(msg`Private cloud devices`)}</h2>
        <Show when={devices.isRefetching}>
          <Loader2 class="h-4 w-4 animate-spin text-muted-foreground" />
        </Show>
      </div>
      <p class="text-sm text-muted-foreground">
        {t(
          msg`Your devices sync folders directly with each other — no third party ever holds your files. Pair a new device with a one-shot code; revoke one and it stops receiving immediately.`,
        )}
      </p>

      <div class="grid gap-4 md:grid-cols-2">
        {/* Issue a pairing code */}
        <Card>
          <CardHeader>
            <CardTitle class="flex items-center gap-2 text-base">
              <KeyRound class="h-4 w-4 text-primary" />
              {t(msg`Add a device`)}
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-3">
            <p class="text-xs text-muted-foreground">
              {t(
                msg`Generate a one-shot code and enter it on the new device. It expires in 5 minutes.`,
              )}
            </p>
            <Button
              onClick={() => void issuePairing()}
              disabled={issuing()}
              data-testid="p2p-cloud-pairing-create"
              class="gap-2"
            >
              <Show when={issuing()}>
                <Loader2 class="h-4 w-4 animate-spin" />
              </Show>
              {t(msg`Generate pairing code`)}
            </Button>
            <Show when={pairing()}>
              {(p) => (
                <div class="rounded-lg border bg-muted/40 p-3">
                  <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(msg`Pairing code`)}
                  </p>
                  <p
                    class="mt-1 select-all break-all font-mono text-sm font-bold"
                    data-testid="p2p-cloud-pairing-token"
                  >
                    {p().token}
                  </p>
                </div>
              )}
            </Show>
          </CardContent>
        </Card>

        {/* Join with a code */}
        <Card>
          <CardHeader>
            <CardTitle class="flex items-center gap-2 text-base">
              <Laptop class="h-4 w-4 text-primary" />
              {t(msg`This is the new device`)}
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-3">
            <div class="space-y-2">
              <Label for="p2p-cloud-pair-token" class="text-xs font-semibold">
                {t(msg`Pairing code`)}
              </Label>
              <TextField
                id="p2p-cloud-pair-token"
                autocomplete="off"
                autocapitalize="none"
                spellcheck={false}
                value={joinToken()}
                onInput={(e) => setJoinToken(e.currentTarget.value)}
                data-testid="p2p-cloud-pair-token"
              />
            </div>
            <div class="space-y-2">
              <Label for="p2p-cloud-pair-name" class="text-xs font-semibold">
                {t(msg`Device name`)}
              </Label>
              <TextField
                id="p2p-cloud-pair-name"
                autocomplete="off"
                value={joinName()}
                onInput={(e) => setJoinName(e.currentTarget.value)}
                data-testid="p2p-cloud-pair-name"
              />
            </div>
            <Button
              onClick={() => void join()}
              disabled={joining() || !joinToken().trim() || !joinName().trim()}
              data-testid="p2p-cloud-pair-submit"
              class="gap-2"
            >
              <Show when={joining()}>
                <Loader2 class="h-4 w-4 animate-spin" />
              </Show>
              {t(msg`Pair this device`)}
            </Button>
            <Show when={joinError()}>
              <p class="text-sm text-destructive" data-testid="p2p-cloud-pair-error">
                {joinError()}
              </p>
            </Show>
            <Show when={joined()}>
              <p class="text-sm text-primary" data-testid="p2p-cloud-pair-success">
                {t(msg`Device paired. It is now trusted in your private cloud.`)}
              </p>
            </Show>
          </CardContent>
        </Card>
      </div>

      {/* Trusted device set */}
      <section class="space-y-3">
        <h3 class="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {t(msg`Trusted devices`)}
        </h3>
        {/* P23/I3: «no tienes dispositivos emparejados» y «no pude preguntar»
            son afirmaciones distintas, y aquí la primera es una mentira
            operativa: el usuario puede creer que perdió la confianza de un
            dispositivo que sigue registrado. En error `isLoading` es FALSE, así
            que el guard de abajo dejaba pasar, `data` era undefined y la
            pantalla pintaba el vacío. Hay que ENVOLVER el bloque entero: un
            `when={!isLoading && !isError}` con fallback de esqueleto pintaría
            error Y esqueleto a la vez, para siempre. */}
        <Show when={devices.isError}>
          <QueryErrorState
            testId="p2p-cloud-devices-error"
            compact
            onRetry={() => void devices.refetch()}
          />
        </Show>
        <Show when={!devices.isError}>
          <Show when={!devices.isLoading} fallback={<Skeleton class="h-20 w-full rounded-xl" />}>
            <Show
              when={(devices.data?.length ?? 0) > 0}
              fallback={
                <div
                  class="flex flex-col items-center justify-center rounded-xl border border-dashed p-10 text-center"
                  data-testid="p2p-cloud-devices-empty"
                >
                  <Laptop class="mb-3 h-8 w-8 text-muted-foreground opacity-20" />
                  <p class="text-sm text-muted-foreground">
                    {t(msg`No devices paired yet. Generate a code to add your first one.`)}
                  </p>
                </div>
              }
            >
              <div class="grid gap-3">
                <For each={devices.data ?? []}>
                  {(device) => (
                    <Card data-testid="p2p-cloud-device">
                      <CardContent class="flex items-center gap-4 p-4">
                        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                          <Laptop class="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div class="flex-1">
                          <h4 class="font-bold" data-testid="p2p-cloud-device-name">
                            {device.name}
                          </h4>
                          <p class="text-xs text-muted-foreground font-mono">{device.device_id}</p>
                        </div>
                        <Badge
                          variant={device.revoked ? "destructive" : "outline"}
                          data-testid="p2p-cloud-device-status"
                          class="gap-1"
                        >
                          <Show when={device.revoked} fallback={<ShieldCheck class="h-3 w-3" />}>
                            <ShieldOff class="h-3 w-3" />
                          </Show>
                          {device.revoked ? t(msg`Revoked`) : t(msg`Trusted`)}
                        </Badge>
                        <Show when={!device.revoked}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void revoke(device.device_id)}
                            data-testid="p2p-cloud-device-revoke"
                          >
                            {t(msg`Revoke`)}
                          </Button>
                        </Show>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </div>
            </Show>
          </Show>
        </Show>
      </section>
    </div>
  );
}
